// apps/web/src/features/auth/components/PasswordField.tsx
// Legacy <Input> + overlay icons. Layout invariant: the icons stack at the
// trailing edge of the input in the order [generate, copy, reveal] so the
// reveal toggle stays anchored next to the input glyph it controls.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '../../../ui/index.js';
import { IconButton } from '../../../ui/index.js';
import { useToastStore } from '../../../stores/toast.js';
import { generateSecurePassword } from '../lib/generateSecurePassword.js';

export interface PasswordFieldProps {
  value: string;
  onChange: (v: string) => void;
  invalid?: boolean;
  hint?: string;
  showGenerate?: boolean;
  showCopy?: boolean;
  // INVARIANT: clicking generate always calls `onChange(next)` so the value
  // lands in the field even when no host hook is wired. `onGenerate` is an
  // optional extra side-effect channel (e.g. an RHF Controller that wants
  // to mark the field dirty/touched after a programmatic value set).
  onGenerate?: (value: string) => void;
  // Forwarded to the underlying <input>. Reset / change-password flows must
  // pass "new-password" so password managers offer to save the new value.
  autoComplete?: string;
}

const ICON_BUTTON_SLOT = 28;

export function PasswordField({
  value,
  onChange,
  invalid,
  hint,
  showGenerate,
  showCopy,
  onGenerate,
  autoComplete,
}: PasswordFieldProps) {
  const { t } = useTranslation('auth');
  const showToast = useToastStore((s) => s.show);
  const [revealed, setRevealed] = useState(false);
  const trailingSlots = (showGenerate ? 1 : 0) + (showCopy ? 1 : 0) + 1;
  const handleGenerate = () => {
    const next = generateSecurePassword();
    onChange(next);
    onGenerate?.(next);
  };
  const handleCopy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      showToast({ tone: 'success', text: t('password.copied') });
    } catch {
      // WHY: clipboard API can reject (insecure context, denied permission,
      // missing API in jsdom). Surface a localized toast — never throw.
      showToast({ tone: 'error', text: t('password.copyFailed') });
    }
  };
  return (
    <div>
      <div style={{ position: 'relative' }}>
        <Input
          value={value}
          onChange={onChange}
          type={revealed ? 'text' : 'password'}
          invalid={invalid ?? false}
          autoComplete={autoComplete}
          style={{ paddingRight: 4 + trailingSlots * ICON_BUTTON_SLOT }}
        />
        <div
          style={{
            position: 'absolute',
            right: 4,
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            gap: 0,
          }}
        >
          {showGenerate && (
            <IconButton icon="refresh-cw" label={t('password.generate')} onClick={handleGenerate} />
          )}
          {showCopy && <IconButton icon="copy" label={t('password.copy')} onClick={handleCopy} />}
          <IconButton
            icon={revealed ? 'eye-off' : 'eye'}
            label={t('password.revealToggle')}
            onClick={() => setRevealed((v) => !v)}
          />
        </div>
      </div>
      {hint && <div style={{ fontSize: 12, color: 'var(--fg-3)', marginTop: 4 }}>{hint}</div>}
    </div>
  );
}
